<template>
  <Container
    :class="$style.container"
    data-test-id="HomeSettingsMainPanel__container"
  >
    <ButtonBack
      :class="$style.headerContainer"
      text="Home"
      :to="$options.homeRoute"
    />
    <Container :class="$style.gridContainerHeading">
      <HeadingMedium
        :class="$style.heading"
        hasMargin
        weight="bold"
      >
        Configure and Customize Widgets
      </HeadingMedium>
      <ParagraphBody
        :class="$style.heading"
      >
        {{ homeSettingsText.subheaderText }}
      </ParagraphBody>
    </Container>
    <div :class="$style.widgetBox">
      <Container :class="$style.gridContainer">
        <GridAuto minWidth="30rem">
          <CardSettings :title="widgetNames.consoleStats">
            <Checkbox
              v-for="(orgStat,index) in orgStats"
              :key="index"
              :class="$style.checkBoxStyle"
              :data-test-id="`HomeSettingsMainPanel__${orgStat.input}`"
              :indeterminate="false"
              :isDisabled="false"
              :label="orgStat.label"
              :value="orgStat.value"
              @input="value => handleConsoleStatsChange(orgStat.input, value)"
            />
          </CardSettings>
          <CardSettings :title="widgetNames.userLockouts">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${userLockouts}`"
                :isDisabled="false"
                :value="orgInsights.userLockouts.visible"
                @input="(value) => handleToggleChange(userLockouts, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.userLockouts }}
            </p>
          </CardSettings>
          <CardSettings :title="widgetNames.expiredPasswords">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${expiredPasswords}`"
                :isDisabled="false"
                :value="orgInsights.expiredPasswords.visible"
                @input="(value) => handleToggleChange(expiredPasswords, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.expiredPasswords }}
            </p>
          </CardSettings>
          <CardSettings :title="widgetNames.upcomingPasswordExpiration">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${upcomingPasswordExpiration}`"
                :isDisabled="false"
                :value="orgInsights.upcomingPasswordExpiration.visible"
                @input="(value) => handleToggleChange(upcomingPasswordExpiration, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.upcomingPasswordExpiration }}
            </p>
          </CardSettings>
          <CardSettings :title="widgetNames.newUsers">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${newUsers}`"
                :isDisabled="false"
                :value="orgInsights.newUsers.visible"
                @input="(value) => handleToggleChange(newUsers, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.newUsers }}
            </p>
          </CardSettings>

          <CardSettings
            :title="widgetNames.reportsReadyForDownload"
          >
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${reportsReadyForDownload}`"
                :isDisabled="false"
                :value="orgInsights.reportsReadyForDownload.visible"
                @input="(value) => handleToggleChange(reportsReadyForDownload, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.reportsReadyForDownload }}
            </p>
          </CardSettings>
          <CardSettings
            :title="widgetNames.userGroupSuggestions"
          >
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${userGroupSuggestions}`"
                :value="orgInsights.userGroupSuggestions.visible"
                @input="(value) => handleToggleChange(userGroupSuggestions, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.userGroupSuggestions }}
            </p>
          </CardSettings>
          <CardSettings
            v-if="showMdmCertificateConfig"
            :title="widgetNames.mdmCertificateExpirations"
          >
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${mdmCertificateExpirations}`"
                :isDisabled="false"
                :value="orgInsights.mdmCertificateExpirations.visible"
                @input="(value) => handleToggleChange(mdmCertificateExpirations, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.mdmCertificateExpirations }}
            </p>
          </CardSettings>
        </GridAuto>
      </Container>
      <Container :class="$style.gridContainer2">
        <GridAuto minWidth="30rem">
          <CardSettings
            :title="widgetNames.ScheduledUserAccess"
          >
            <Checkbox
              :key="userState.scheduledUserSuspensions.label"
              :class="$style.checkBoxStyle"
              :indeterminate="false"
              :isDisabled="false"
              :label="userState.scheduledUserSuspensions.label"
              :value="userState.scheduledUserSuspensions.value"
              @input="value =>
                handleUserStateChange(userState.scheduledUserSuspensions, value)"
            />
            <InputNumberInline
              v-if="userState.scheduledUserSuspensions.value"
              :key="userState.scheduledUserSuspensions.text"
              :class="$style.input"
              :name="userState.scheduledUserSuspensions.label"
              :required="true"
              rules="between:1,90|integer|required"
              showValidationMessage
              :textBefore="userState.scheduledUserSuspensions.text"
              textAfter=" days"
              :value="suspensionsDays"
              @input="value =>
                handleDaysChange(userState.scheduledUserSuspensions.name, value)"
            />
            <Checkbox
              :key="userState.scheduledUserActivations.label"
              :class="$style.checkBoxStyle"
              :indeterminate="false"
              :isDisabled="false"
              :label="userState.scheduledUserActivations.label"
              :value="userState.scheduledUserActivations.value"
              @input="value =>
                handleUserStateChange(userState.scheduledUserActivations, value)"
            />
            <InputNumberInline
              v-if="userState.scheduledUserActivations.value"
              :key="userState.scheduledUserActivations.text"
              :class="$style.input"
              :name="userState.scheduledUserActivations.label"
              :required="true"
              rules="between:1,90|integer|required"
              showValidationMessage
              :textBefore="userState.scheduledUserActivations.text"
              textAfter=" days"
              :value="activationsDays"
              @input="value => handleDaysChange(userState.scheduledUserActivations.name, value)"
            />
          </CardSettings>
          <CardSettings :title="widgetNames.deviceNotifications">
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.deviceNotifications }}
            </p>
            <Checkbox
              :class="$style.checkBoxStyle"
              data-test-id="HomeSettingsMainPanel__deviceInactiveMetric"
              :indeterminate="false"
              :isDisabled="false"
              :label="deviceMetricNames.inactiveMetric"
              :value="orgInsights.deviceNotifications.inactiveMetric.visible"
              @input="(value) =>
                handleDeviceNotificationsChange(deviceMetrics.inactiveMetric, value)"
            />
            <Checkbox
              v-if="hasSystemInsights"
              :class="$style.checkBoxStyle"
              data-test-id="HomeSettingsMainPanel__deviceUptimeMetric"
              :disabled="false"
              :indeterminate="false"
              :label="deviceMetricNames.uptimeMetric"
              :value="orgInsights.deviceNotifications.uptimeMetric.visible"
              @input="value =>
                handleDeviceNotificationsChange(deviceMetrics.uptimeMetric, value)"
            />
            <InputNumberInline
              v-if="hasSystemInsights
                && orgInsights.deviceNotifications.uptimeMetric.visible"
              :class="$style.input"
              name="Device Uptime Metric"
              :required="true"
              rules="between:1,30|integer|required"
              showValidationMessage
              textAfter=" days"
              textBefore="See devices uptime based on a customizable timeframe"
              :value="upTimeDays"
              @input="value => handleUptimeDaysChange(deviceMetrics.uptimeMetric, value)"
            />
          </CardSettings>
          <CardSettings :title="widgetNames.userNotifications">
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.userNotifications }}
            </p>
            <Checkbox
              v-for="(notification,index) in notifications"
              :key="index"
              :class="$style.checkBoxStyle"
              :data-test-id="`HomeSettingsMainPanel__${notification.input}`"
              :indeterminate="false"
              :isDisabled="false"
              :label="notification.label"
              :value="notification.value"
              @input="value => handleUserNotificationsChange(notification.input, value)"
            />
          </CardSettings>
          <CardSettings title="Recent OS Releases">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${newOSReleases}`"
                :isDisabled="false"
                :value="orgInsights.newOSReleases.visible"
                @input="(value) => handleToggleChange(newOSReleases, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.newOSReleases }}
            </p>
          </CardSettings>
          <CardSettings
            :title="widgetNames.manualGroupMembership"
          >
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${manualGroupMembership}`"
                :value="orgInsights.manualGroupMembership.visible"
                @input="(value) => handleToggleChange(manualGroupMembership, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.manualGroupMembership }}
            </p>
          </CardSettings>
          <CardSettings :title="widgetNames.learningCenter">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${learningCenter}`"
                :isDisabled="false"
                :value="orgInsights.learningCenter.visible"
                @input="(value) => handleToggleChange(learningCenter, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.learningCenter }}
            </p>
          </CardSettings>
          <CardSettings :title="widgetNames.jcUniversity">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${jcUniversity}`"
                :isDisabled="false"
                :value="orgInsights.jcUniversity.visible"
                @input="(value) => handleToggleChange(jcUniversity, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.jcUniversity }}
            </p>
          </CardSettings>
          <CardSettings :title="widgetNames.officeHours">
            <template v-slot:title>
              <ToggleSwitch
                :class="$style.switch"
                :data-test-id="`HomeSettingsMainPanel__${officeHours}`"
                :isDisabled="false"
                :value="orgInsights.officeHours.visible"
                @input="(value) => handleToggleChange(officeHours, value)"
              />
            </template>
            <p :class="$style.subtitle">
              {{ homeSettingsText.subtitles.officeHours }}
            </p>
          </CardSettings>
        </GridAuto>
      </Container>
    </div>
  </Container>
</template>

<script>
import
{
  ButtonBack,
  Checkbox,
  Container,
  GridAuto,
  HeadingMedium,
  InputNumberInline,
  ParagraphBody,
  ToggleSwitch,
}
  from '@jumpcloud/ui-components';
import { get } from 'lodash';
import { mapActions, mapGetters, mapState } from 'vuex';
import { notify } from 'client/util';
import CardSettings from 'client/components/CardSettings.vue';
import Constants, {
  ItemTypeConsts,
  ItemTypeRoutes,
  StoreModuleMap,
} from 'client/util/Constants';
import CustomizableDashboardMixin from 'client/Discover/mixins/CustomizableDashboardMixin';
import FundamentalsVisibilityMixin from 'client/components/mixins/fundamentalsVisibilityMixin';
import HomeWidgetConsts from 'client/HomePageV2/constants/HomeWidgetConsts';
import MainPanelMixin from 'components/MainPanel/mixins/MainPanelMixin';
import OptimizelyHelper from 'client/GrowthEngineering/OptimizelyHelper';
import SegmentEventMixin from 'client/HomePageV2/mixins/SegmentEventMixin';
import SettingsPageMixin from 'client/Home/mixins/SettingsPageMixin';

const userDisplayPreferencesModule = StoreModuleMap[ItemTypeConsts.userDisplayPreferences];
const { error, success } = Constants;
const administratorModel = 'AdministratorModel';
const settingsModule = StoreModuleMap[ItemTypeConsts.settings];
const {
  consoleStatsMetrics,
  consoleStatsNames,
  deviceMetrics,
  deviceMetricNames,
  userNotificationMetrics,
  userNotificationMetricsNames,
  userStateMetrics,
  userStateMetricsNames,
  homeSettingsText,
  widgetNames,
  widgetTypes,
} = HomeWidgetConsts;

const {
  deviceNotifications,
  expiredPasswords,
  fundamentals,
  guidedSetup,
  jcUniversity,
  learningCenter,
  manualGroupMembership,
  mdmCertificateExpirations,
  newOSReleases,
  newUsers,
  officeHours,
  reportsReadyForDownload,
  tutorialResources,
  upcomingPasswordExpiration,
  userGroupSuggestions,
  userLockouts,
  userNotifications,
  scheduledUserSuspensions,
  scheduledUserActivations,
} = widgetTypes;

export default {
  name: 'HomeSetting2ColumnPanel',

  components: {
    ButtonBack,
    Checkbox,
    Container,
    CardSettings,
    GridAuto,
    HeadingMedium,
    InputNumberInline,
    ParagraphBody,
    ToggleSwitch,
  },

  mixins: [
    CustomizableDashboardMixin,
    FundamentalsVisibilityMixin,
    MainPanelMixin,
    SettingsPageMixin,
    SegmentEventMixin,
  ],

  OptimizelyHelper,
  homeRoute: ItemTypeRoutes[ItemTypeConsts.home],

  data() {
    return {
      consoleStatsMetrics,
      deviceMetrics,
      deviceMetricNames,
      deviceNotifications,
      homeSettingsText,
      expiredPasswords,
      fundamentals,
      guidedSetup,
      jcUniversity,
      learningCenter,
      manualGroupMembership,
      mdmCertificateExpirations,
      newOSReleases,
      newUsers,
      officeHours,
      reportsReadyForDownload,
      resources: {},
      tutorialResources,
      scheduledUserSuspensions,
      scheduledUserActivations,
      settingsRoute: ItemTypeRoutes[ItemTypeConsts.settingsHome],
      activationsDays: 0,
      suspensionsDays: 0,
      upcomingPasswordExpiration,
      userGroupSuggestions,
      upTimeDays: 0,
      userLockouts,
      userNotificationMetrics,
      userStateMetrics,
      userNotifications,
      widgetNames,
    };
  },

  computed: {
    ...mapGetters(settingsModule, ['hasSystemInsights']),

    // Admin Level widget setting
    ...mapState(userDisplayPreferencesModule, {
      displaySettings: state => state.displayPreferencesData,
    }),

    ...mapState(administratorModel, [
      'mdmVisibility',
    ]),

    areDeviceControlsVisible() {
      const availableMetrics = [
        deviceMetrics.inactiveMetric,
        deviceMetrics.uptimeMetric,
      ];
      return this.checkIfAllMetricsAreHidden(
        this.orgInsights.deviceNotifications,
        availableMetrics,
      );
    },

    areUserNotificationControlsVisible() {
      const availableMetrics = [
        userNotificationMetrics.usersWithSambaAccess,
        userNotificationMetrics.usersWithoutPassword,
        userNotificationMetrics.userWithAdminSudoPasswordlessAccess,
        userNotificationMetrics.usersWithAdminSudoAccess,
      ];
      return this.checkIfAllMetricsAreHidden(this.orgInsights.userNotifications, availableMetrics);
    },

    notifications() {
      return [
        {
          input: userNotificationMetrics.usersWithAdminSudoAccess,
          label: 'Users with global admin sudo access',
          value: this.orgInsights.userNotifications.usersWithAdminSudoAccess.visible,
        },
        {
          input: userNotificationMetrics.userWithAdminSudoPasswordlessAccess,
          label: 'Users with global admin sudo passwordless access',
          value: this.orgInsights.userNotifications.userWithAdminSudoPasswordlessAccess.visible,
        },
        {
          input: userNotificationMetrics.usersWithoutPassword,
          label: 'Users with no password on file',
          value: this.orgInsights.userNotifications.usersWithoutPassword.visible,
        },
        {
          input: userNotificationMetrics.usersWithSambaAccess,
          label: 'Users with samba access',
          value: this.orgInsights.userNotifications.usersWithSambaAccess.visible,
        },
      ];
    },

    orgInsights() {
      return this.displaySettings.display;
    },

    orgStats() {
      return [
        {
          input: consoleStatsMetrics.totalUsers,
          label: consoleStatsNames.totalUsers,
          value: this.orgInsights.consoleStats.totalUsers.visible,
        },
        {
          input: consoleStatsMetrics.totalGroups,
          label: consoleStatsNames.totalGroups,
          value: this.orgInsights.consoleStats.totalGroups.visible,
        },
        {
          input: consoleStatsMetrics.totalDevices,
          label: consoleStatsNames.totalDevices,
          value: this.orgInsights.consoleStats.totalDevices.visible,
        },
        {
          input: consoleStatsMetrics.totalApplications,
          label: consoleStatsNames.totalApplications,
          value: this.orgInsights.consoleStats.totalApplications.visible,
        },
        {
          input: consoleStatsMetrics.totalConfigurations,
          label: consoleStatsNames.totalConfigurations,
          value: this.orgInsights.consoleStats.totalConfigurations.visible,
        },
      ];
    },

    showMdmCertificateConfig() {
      return this.mdmVisibility;
    },

    userState() {
      return {
        scheduledUserSuspensions: {
          name: 'scheduledUserSuspensions',
          label: userStateMetricsNames.suspensions,
          text: this.homeSettingsText.subtitles.scheduledUserSuspensions,
          value: this.orgInsights.scheduledUserSuspensions.visible,
        },
        scheduledUserActivations: {
          name: 'scheduledUserActivations',
          label: userStateMetricsNames.activations,
          text: this.homeSettingsText.subtitles.scheduledUserActivations,
          value: this.orgInsights.scheduledUserActivations.visible,
        },
      };
    },
  },

  updated() {
    const { visible: devicesVisible } = this.orgInsights.deviceNotifications;
    if (!this.areDeviceControlsVisible && devicesVisible) {
      this.handleToggleChange(this.deviceNotifications, false);
    }

    const { visible: userNotificationVisible } = this.orgInsights.userNotifications;
    if (!this.areUserNotificationControlsVisible && userNotificationVisible) {
      this.handleToggleChange(this.userNotifications, false);
    }
  },

  created() {
    this.handleCreate();
  },

  methods: {
    ...mapActions(userDisplayPreferencesModule, [
      'fetchDisplayPreferencesData',
      'setConsoleStatsVisibility',
      'setDeviceNotificationMetricVisibility',
      'setConsoleStatsVisibility',
      'setUserNotificationMetricVisibility',
      'setHomeWidgetVisibility',
      'setUpTimeDays',
      'setUserStateDays',
      'setUserStateVisibility',
    ]),

    checkIfAllMetricsAreHidden(allMetrics, availableMetrics) {
      const allValuesAreHidden = availableMetrics.every(
        metric => !allMetrics[metric].visible,
      );

      if (allValuesAreHidden) {
        return false;
      }
      return true;
    },

    handleConsoleStatsChange(field, isVisible) {
      this.setConsoleStatsVisibility({
        widgetType: field,
        visible: isVisible,
      }).then(() => {
        notify(success, 'Settings have been updated.');
      })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });
      this.triggerCheckboxClickedHandler('organizational stats', isVisible, consoleStatsNames[field]);
    },

    async handleCreate() {
      await this.fetchDisplayPreferencesData();
      this.activationsDays = this.displaySettings
        .scheduledUserActivations?.numberOfDaysToShow;

      this.suspensionsDays = this.displaySettings
        .scheduledUserSuspensions?.numberOfDaysToShow;

      this.upTimeDays = this.displaySettings.display.deviceNotifications.uptimeMetric
        .additionalDisplaySettings?.numberOfDaysToShow;
    },

    handleDaysChange(field, value) {
      if (value > 0 && value <= 90 && Number.isInteger(parseFloat(value))) {
        this.handleSetDays(field, value, true);
      } else if (field === this.userState.scheduledUserActivations.name) {
        this.activationsDays = value;
        this.handleSetDays(field, 90, false);
      } else {
        this.handleSetDays(field, 90, false);
        this.suspensionsDays = value;
      }
    },

    handleDeviceNotificationsChange(field, value) {
      if (!this.areDeviceControlsVisible && value === true) {
        this.handleToggleChange(this.deviceNotifications, true);
      }
      this.setDeviceNotificationMetricVisibility({
        widgetType: field,
        visible: value,
      }).then(() => {
        notify(success, 'Settings have been updated.');
      })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });
      this.triggerCheckboxClickedHandler('device notifications', value, deviceMetricNames[field]);
    },

    handleSetDays(field, value, showNotifications) {
      this.setUserStateDays({
        widgetType: field,
        numberOfDaysToShow: value,
      }).then(() => {
        if (showNotifications) {
          if (field === this.userState.scheduledUserActivations.name) {
            this.activationsDays = value;
          } else {
            this.suspensionsDays = value;
          }
          notify(success, 'Settings have been updated.');
        }
      })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });
    },

    handleSetUptimeDays(field, value, showNotifications) {
      this.setUpTimeDays({
        widgetType: field,
        numberOfDaysToShow: value,
      }).then(() => {
        if (showNotifications) {
          this.upTimeDays = value;
          notify(success, 'Settings have been updated.');
        }
      })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });
    },

    handleToggleChange(field, value) {
      this.setHomeWidgetVisibility({
        widgetType: field,
        visible: value,
      }).then(() => {
        notify(success, 'Settings have been updated.');
      })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });
      this.triggerToggleClickedHandler(field, value);
    },

    handleUptimeDaysChange(field, value) {
      if (value > 0 && value <= 30 && Number.isInteger(parseFloat(value))) {
        this.handleSetUptimeDays(field, value, true);
      } else {
        this.upTimeDays = value;
        this.handleSetUptimeDays(field, 30, false);
      }
    },

    handleUserNotificationsChange(field, value) {
      if (!this.areUserNotificationControlsVisible && value) {
        this.handleToggleChange(this.userNotifications, true);
      }

      this.setUserNotificationMetricVisibility({
        widgetType: field,
        visible: value,
      }).then(() => {
        notify(success, 'Settings have been updated.');
      })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });
      this.triggerCheckboxClickedHandler('user notifications', value, userNotificationMetricsNames[field]);
    },

    handleUserStateChange(field, value) {
      this.setUserStateVisibility({
        widgetType: field.name,
        visible: value,
      }).then(() => {
        notify(success, 'Settings have been updated.');
      })
        .catch((err) => {
          const message = get(err, 'response.data.message', 'Unable to save settings.');
          notify(error, message);
          throw new Error(message);
        });

      this.triggerCheckboxClickedHandler('user state access', value, field.label);
    },
  },
};
</script>
<style module scoped>
.container {
  background-color: var(--jc-color-disabled);
  display: inherit;
  padding: var(--jc-spacer-x-small);
}

.widgetBox {
  background-color: var(--jc-color-surface-light);
  display: table;
}

.heading {
  max-width: 40rem;
}

.gridContainerHeading {
  background-color: var(--jc-color-surface-light);
  max-width: 88rem;
}

.gridContainer {
  background-color: var(--jc-color-surface-light);
  display: table-cell;
  float: left;
  max-width: 44rem;
}

.gridContainer2 {
  background-color: var(--jc-color-surface-light);
  display: table-cell;
  float: left;
  max-width: 44rem;

  --container-padding: var(--jc-spacer-medium) var(--jc-spacer-medium) var(--jc-spacer-medium) 0;
}

.headerContainer {
  margin-bottom: var(--jc-spacer);
}

.switch {
  padding-right: var(--jc-spacer);

  --switch-label-weight: 600;
}

.checkBoxStyle {
  padding: var(--jc-spacer-x-small);
  width: fit-content;
}

.input {
  --inline-input-width: 6ch;
}
</style>
